// Cloned from GreenflexSection.jsx on purpose (RES022 isolation pattern used
// throughout this codebase) -- only the API prefix differs (/greenflex-res022).
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Send,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import GreenflexReemplazoRes022 from "./GreenflexReemplazoRes022";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GreenflexSectionRes022 = ({ dossierId, dossier }) => {
  const [checkData, setCheckData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [excludedOptionals, setExcludedOptionals] = useState(new Set());

  const fetchReadiness = useCallback(async () => {
    if (!dossierId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/greenflex-res022/${dossierId}/check-ready`);
      setCheckData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [dossierId]);

  useEffect(() => {
    fetchReadiness();
  }, [fetchReadiness]);

  const handleSend = async () => {
    if (sending) return;

    if (checkData?.already_sent) {
      const gfData = checkData.greenflex || {};
      const sentDate = gfData.sent_at
        ? new Date(gfData.sent_at).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
        : "fecha desconocida";
      const confirmed = window.confirm(
        `Este expediente ya fue enviado a Greenflex el ${sentDate} (Acción: ${gfData.action_id || "?"}).\n\n¿Está seguro de que desea reenviar? Se creará una nueva acción en Greenflex.`
      );
      if (!confirmed) return;
    }

    setSending(true);
    setSendResult(null);
    try {
      const enviar = (extra) => axios.post(`${API}/greenflex-res022/${dossierId}/send`, {
        excluded_optionals: Array.from(excludedOptionals),
        ...extra,
      });
      let res;
      try {
        res = await enviar({});
      } catch (err) {
        const d = err.response?.data?.detail;
        if (err.response?.status !== 409 || !d?.confirmar) throw err;
        if (!window.confirm(`⚠ ${d.mensaje}`)) return;
        res = await enviar({ confirmar: true });
      }
      setSendResult(res.data);
      if (res.data.success && !res.data.partial) {
        toast.success(res.data.message);
      } else if (res.data.partial) {
        toast.warning(res.data.message);
      }
      fetchReadiness();
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al enviar a Greenflex";
      toast.error(msg);
      setSendResult({ success: false, message: msg });
    } finally {
      setSending(false);
    }
  };

  const allReady = checkData?.all_ready;
  const alreadySent = checkData?.already_sent;
  const docs = checkData?.documents || [];
  const gf = checkData?.greenflex || {};
  const requiredDocs = docs.filter((d) => d.required);
  const optionalDocs = docs.filter((d) => !d.required);
  const readyCount = requiredDocs.filter((d) => d.ready).length;

  return (
    <Card data-testid="greenflex-section-res022">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5 text-emerald-600" />
            Greenflex (eCAEs)
            {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-normal text-slate-500">
              {readyCount}/{requiredDocs.length} docs
            </span>
            <Button
              variant="ghost"
              size="sm"
              data-testid="greenflex-refresh"
              onClick={fetchReadiness}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {alreadySent && (
          <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="greenflex-sent-banner">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-emerald-800">
                Envoyé le {new Date(gf.sent_at).toLocaleDateString("fr-FR")}
              </p>
              <p className="text-xs text-emerald-600 truncate">
                Action ID: <a href={`https://caes.efinovatic.es/admin/gestion/actuacion/?q=${gf.action_id}`} target="_blank" rel="noreferrer" className="underline font-medium">{gf.action_id}</a> | Ref: {gf.reference_sent}
              </p>
              {gf.errors?.length > 0 && (
                <p className="text-xs text-orange-600 mt-1">
                  {gf.errors.length} erreur(s) d'upload
                </p>
              )}
            </div>
          </div>
        )}

        {alreadySent && <GreenflexReemplazoRes022 dossierId={dossierId} />}

        <Button
          className={`w-full ${
            allReady
              ? "bg-emerald-600 hover:bg-emerald-700 text-white"
              : "bg-slate-200 text-slate-500 cursor-not-allowed"
          }`}
          disabled={!allReady || sending}
          onClick={handleSend}
          data-testid="greenflex-send-btn"
        >
          {sending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Envoi en cours...
            </>
          ) : alreadySent ? (
            <>
              <Send className="w-4 h-4 mr-2" />
              Renvoyer à Greenflex
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Envoyer à Greenflex
            </>
          )}
        </Button>

        {sendResult && (
          <div
            className={`p-3 rounded-lg border text-sm ${
              sendResult.success && !sendResult.partial
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : sendResult.partial
                ? "bg-orange-50 border-orange-200 text-orange-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
            data-testid="greenflex-send-result"
          >
            {sendResult.message}
            {sendResult.errors?.map((e, i) => (
              <p key={i} className="text-xs mt-1 opacity-80">{e}</p>
            ))}
          </div>
        )}

        <button
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 w-full"
          onClick={() => setExpanded(!expanded)}
          data-testid="greenflex-toggle-checklist"
        >
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? "Masquer" : "Voir"} la checklist des documents
        </button>

        {expanded && (
          <div className="space-y-1" data-testid="greenflex-checklist">
            <p className="text-xs font-semibold text-slate-600 mt-2">Obligatoires :</p>
            {requiredDocs.map((doc) => (
              <div
                key={doc.key}
                className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm ${
                  doc.ready ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                }`}
              >
                {doc.ready ? (
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                ) : (
                  <XCircle className="w-3.5 h-3.5 shrink-0" />
                )}
                <span className="truncate">{doc.label}</span>
              </div>
            ))}
            {optionalDocs.length > 0 && (
              <>
                <p className="text-xs font-semibold text-slate-600 mt-3">Optionnels (selon région) :</p>
                {optionalDocs.map((doc) => {
                  const isExcluded = excludedOptionals.has(doc.key);
                  return (
                    <div
                      key={doc.key}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer select-none ${
                        isExcluded ? "bg-slate-50 text-slate-400 line-through" : doc.ready ? "bg-blue-50 text-blue-700" : "bg-slate-50 text-slate-500"
                      }`}
                      onClick={() => {
                        setExcludedOptionals((prev) => {
                          const next = new Set(prev);
                          if (next.has(doc.key)) next.delete(doc.key);
                          else next.add(doc.key);
                          return next;
                        });
                      }}
                      data-testid={`greenflex-optional-toggle-${doc.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={!isExcluded}
                        readOnly
                        className="w-3.5 h-3.5 shrink-0 accent-blue-600 cursor-pointer"
                      />
                      <span className="truncate">{doc.label}</span>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GreenflexSectionRes022;
